import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  public url: string = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getOpts() {
    const userJSON = localStorage.getItem('userDB');
    const tokenRaw = localStorage.getItem('token');

    if (!userJSON && !tokenRaw) return;

    const safeParse = (s: string | null): any => {
      if (!s) return null;
      try { return JSON.parse(s); } catch { return null; }
    };

    const tokenParsed = safeParse(tokenRaw);
    const userParsed = safeParse(userJSON);

    // localStorage.token may legitimately hold a raw JWT (older flows / Firebase
    // SDK overwrites) — fall back to that string when JSON parsing fails.
    const token =
      tokenParsed?.accessToken ||
      userParsed?.token?.accessToken ||
      (tokenRaw && !tokenParsed ? tokenRaw : undefined);

    if (!token) return;
    return { headers: { authorization: 'Bearer ' + token } };
  }

  /**
   * Adds the signed-in user's token to API calls that don't pass one. Several
   * screens call get/post/put without getOpts(); the backend now requires the
   * token on most routes, and relying on the interceptor's 401 → refresh →
   * replay logs the user out whenever the refresh can't run.
   */
  private withAuth(options?: any): any {
    const headers = options?.headers;
    const hasAuth =
      !!headers &&
      (typeof headers.has === 'function'
        ? headers.has('authorization') || headers.has('Authorization')
        : !!(headers.authorization || headers.Authorization));
    if (hasAuth) return options;
    const auth = this.getOpts();
    if (!auth) return options;
    if (headers && typeof headers.set === 'function') {
      return { ...options, headers: headers.set('authorization', auth.headers.authorization) };
    }
    return { ...(options || {}), headers: { ...(headers || {}), ...auth.headers } };
  }

  get(uri: string, options?: any) {
    return this.http.get(this.url + uri, this.withAuth(options));
  }

  getExternal(url: string, options?: any) {
    return this.http.get(url, options);
  }

  post(uri: string, data: any, options?: any) {
    return this.http.post(this.url + uri, data, this.withAuth(options));
  }

  put(uri: string, data: any, options?: any) {
    return this.http.put(this.url + uri, data, this.withAuth(options));
  }

  /**
   * Persist the user's SELECTED address server-side (search & purchases).
   *
   * Prefers the authenticated `PUT user/me/selected-address` (normalizes and
   * returns `{ address, addresses }`). Falls back to the legacy
   * `PUT user/:id` when that endpoint isn't available yet (e.g. the backend
   * change isn't deployed → 404) so the server's `user.address` is updated
   * either way — otherwise the home read-path would re-fetch a stale server
   * address and silently revert the selection. Returns the address to cache.
   */
  async putSelectedAddress(
    userId: string,
    address: any,
    options?: any
  ): Promise<{ address: any; addresses?: any[] }> {
    try {
      const res: any = await this.put('user/me/selected-address', { address }, options).toPromise();
      return {
        address: res?.address || address,
        addresses: Array.isArray(res?.addresses) ? res.addresses : undefined,
      };
    } catch (_) {
      // Legacy endpoint: unauthenticated, merges the body — sets user.address.
      await this.put('user/' + userId, { address }, options).toPromise();
      return { address };
    }
  }

  patch(uri: string, data: any, options?: any) {
    return this.http.patch(this.url + uri, data, this.withAuth(options));
  }

  delete(uri: string, options?: any) {
    return this.http.delete(this.url + uri, this.withAuth(options));
  }
}
